import { useState } from "react";

import { FarcasterIcon } from "@hl/base-components/lib/assets/icons.generated";
import { SECONDARY_COLOR } from "@hl/base-components/lib/theme/button";
import { ReactComponent as CopyIcon } from "@hl/shared-features/lib/assets/icons/copy-address-icon.svg";
import { networkLookup } from "@hl/shared-features/lib/utils/blockExplorer";
import {
  Box,
  Grid,
  Space,
  Button,
  Text,
  Anchor,
  Group,
  Card,
} from "@mantine/core";
import { TwitterShareButton } from "react-share";
import { parseEther } from "viem";

import { NetworkType } from "~/apollo/graphql.generated";

import { ReactComponent as TwitterLogo } from "../../../../../../assets/icons/twitter-logo.svg";

import * as styles from "./styles";

export interface ShareSaleModalData {
  mintUrl: string;
  entity: "mint" | "auction";
  publicPrice?: string;
  chainId: number;
}

const MINT_WITH_WARPS_MAX_PRICE = parseEther("0.004");
const MINT_WITH_WARPS_CHAINS = [
  NetworkType.BASE,
  NetworkType.OPTIMISM,
  NetworkType.ZORA,
];
const WC_TX_CHAINS = [
  // NetworkType.ETHEREUM, not supported yet
  NetworkType.BASE,
  NetworkType.OPTIMISM,
  NetworkType.ZORA,
];

const ShareSaleModal = ({ data }: { data: ShareSaleModalData }) => {
  const [copied, setCopied] = useState<boolean>(false);
  const { entity } = data;

  const onCopyClick = () => {
    navigator.clipboard.writeText(data.mintUrl);
    setCopied(true);
  };
  const network = networkLookup(data.chainId);

  const isMint = entity === "mint";
  const isMintWithWarpsEligible =
    isMint &&
    data.publicPrice !== undefined &&
    MINT_WITH_WARPS_CHAINS.includes(network.type) &&
    parseEther(data.publicPrice) < MINT_WITH_WARPS_MAX_PRICE;

  const isFcTxMintEligible = isMint && WC_TX_CHAINS.includes(network.type);

  return (
    <Box mx="auto">
      <div css={styles.rowStyles}>
        <Text component="label">Copy the link to your {entity} page</Text>
        <Space h="xs" />
        <Card>
          <Grid justify="space-between">
            <Grid.Col span="auto">
              <Text size={14}>{data.mintUrl}</Text>
            </Grid.Col>
            <Grid.Col span="content">
              <Anchor underline={false} size="xs" onClick={onCopyClick}>
                <Group align="center" spacing="xs">
                  <CopyIcon />
                  <Text weight={500} size={14}>
                    {copied ? "Copied!" : "Copy"}
                  </Text>
                </Group>
              </Anchor>
            </Grid.Col>
          </Grid>
        </Card>
      </div>
      <Space h="lg" />
      <Text component="label">Or share to...</Text>
      <Space h="xs" />
      <TwitterShareButton url={data.mintUrl} css={{ width: "100%" }}>
        <Button
          fullWidth
          color={SECONDARY_COLOR}
          size="lg"
          leftIcon={<TwitterLogo />}
        >
          Twitter
        </Button>
      </TwitterShareButton>

      {isMintWithWarpsEligible && (
        <FarcasterSharedButton url={`${data.mintUrl}?fcframe=warps`}>
          Farcaster: mint with warps
        </FarcasterSharedButton>
      )}

      {isFcTxMintEligible && (
        <FarcasterSharedButton url={`${data.mintUrl}?fcframe=tx`}>
          Farcaster: mint with {network.displayName}
        </FarcasterSharedButton>
      )}
      {(isMintWithWarpsEligible || isFcTxMintEligible) && (
        <Box mt="sm">
          <Text size="sm">
            Learn more about{" "}
            <Anchor
              href="https://support.highlight.xyz/knowledge-base/partnerships/farcaster-integration"
              target="_blank"
              rel="noreferrer"
              size="sm"
              fw={500}
            >
              Farcaster frame options
            </Anchor>
          </Text>
        </Box>
      )}
    </Box>
  );
};

const FarcasterSharedButton: React.FC<
  React.PropsWithChildren<{ url: string }>
> = ({ url, children }) => {
  const shareUrls = `https://warpcast.com/~/compose?embeds[]=${encodeURIComponent(
    url
  )}`;
  return (
    <Button
      mt="md"
      component="a"
      target="_blank"
      href={shareUrls}
      fullWidth
      color={SECONDARY_COLOR}
      leftIcon={<FarcasterIcon />}
    >
      {children}
    </Button>
  );
};

export default ShareSaleModal;
